import { useCallback, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { Box, Button, Popover, Slide } from "@mui/material";

import EventIcon from "@mui/icons-material/Event";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import { Availability, TimeSlot } from "src/api/types";
import DateSlide from "./component/DateSlide";
import TimeSlide from "./component/TimeSlide";
import ConfirmSlide from "./component/ConfirmSlide";

type View = "date" | "time" | "confirm";
const ORDER: View[] = ["date", "time", "confirm"];

interface Props {
  availability: Availability;
  label?: string;
}

const SlotPicker = ({ availability, label = "Choose a time" }: Props) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("date"); // "date" | "time"
  const [prevView, setPrevView] = useState<View>("date");
  const [pickedDate, setPickedDate] = useState<Date | null>(null); // Date
  const [pickedTime, setPickedTime] = useState<TimeSlot | null>(null);

  const go = useCallback(
    (next: View) => {
      setPrevView(view);
      setView(next);
    },
    [view],
  );

  const handleOpen = () => {
    go("date");
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDate = (d: Date | null) => {
    setPickedDate(d);
    go("time");
  };

  const handleGoBackToDate = useCallback(() => go("date"), [go]);
  const handleGoBackToTime = useCallback(() => go("time"), [go]);

  const handleTime = (t: TimeSlot) => {
    setPickedTime(t);
    go("confirm");
  };

  const buttonText =
    pickedDate && pickedTime
      ? `${format(pickedDate, "EEE, MMM d")} at ${format(parseISO(pickedTime.start_at), "H:mm")}`
      : label;

  const dirFor = (panel: View): "left" | "right" => {
    const i = ORDER.indexOf(panel);
    const cur = ORDER.indexOf(view);
    if (i === cur) return ORDER.indexOf(prevView) <= cur ? "left" : "right";
    return i < cur ? "right" : "left";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Button
        ref={anchorRef}
        variant="outlined"
        startIcon={<EventIcon />}
        onClick={handleOpen}
      >
        {buttonText}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose} // fires on click-outside and Escape
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: 3 } } }}
      >
        {/* Fixed-size stage so the two panels can slide over each other */}
        <Box
          sx={{
            position: "relative",
            width: 320,
            height: 360,
            overflow: "hidden",
          }}
        >
          <Slide
            in={view === "date"}
            direction={dirFor("date")}
            appear={false}
            mountOnEnter
            unmountOnExit
          >
            <DateSlide
              value={pickedDate}
              onChange={handleDate}
              availability={availability}
            />
          </Slide>

          <Slide
            in={view === "time"}
            direction={dirFor("time")}
            mountOnEnter
            unmountOnExit
          >
            <TimeSlide
              availability={availability}
              pickedDate={pickedDate}
              pickedTime={pickedTime}
              goBack={handleGoBackToDate}
              onChange={handleTime}
            />
          </Slide>

          <Slide
            in={view === "confirm"}
            direction={dirFor("confirm")}
            mountOnEnter
            unmountOnExit
          >
            <ConfirmSlide
              pickedDate={pickedDate}
              pickedTime={pickedTime}
              onClose={handleClose}
              goBack={handleGoBackToTime}
            />
          </Slide>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

/* ---------- Example usage ----------

import SlotPicker from "./SlotPicker";

const availability = {
  "2026-10-07": ["09:00", "09:30", "11:00", "14:30"],
  "2026-10-08": ["10:00", "13:00"],
  "2026-10-12": ["09:00", "15:00", "15:30", "16:00"],
};

function Booking() {
  const [slot, setSlot] = useState(null);
  return <SlotPicker availability={availability} value={slot} onChange={setSlot} />;
}

------------------------------------- */

export default SlotPicker;
